import { has } from 'lodash';
import {
    DistributionRecipientReportApiModel,
    DistributionReportStat,
    IndividualRecipientReportApiModel,
    LinkText,
    RecipientReportSeriesApiModel
} from '../../../model/statisticsReport/distribution-report-stat-api.model';
import { DistributionAccountStatisticsModel } from '../../../model/statisticsReport/distribution-account-statistics.model';
import { AgprMath } from '../../../../Misc/helpers/math/math-helper';
import { EmailRecipientModel } from '../../../model/statisticsReport/email-recipient.model';
import { RecipientNegativeoutcomeStatsModel } from '../../../../../Discover/Targeting/models/recipients-negativeoutcome-stats.model';


export class RecipientReportDataSeries {
    recipientCount: number;
    sentCount: number;
    sentRatio: number;

    deliveredCount: number;
    deliveredRatio: number;

    uniqueOpenCount: number;
    openCount: number;
    uniqueOpenRatio: number;

    uniqueClickCount: number;
    clickCount: number;
    uniqueClickRatio: number;
    reliableUniqueClicks: number;
    mppClicks: number;
    totalMppClicks: number;
    reliableClicks: number;
    reliableUniqueClickRate: number;

    replyCount: number;
    replyRatio: number;

    unopened: number;
    softBounced: number;
    blocked: number;
    unsubscribed: number;
    previousUnsubscribe: number;
    previousSoftBounced: number;
    previousSpamReport: number;
    previousHardBounced: number;
    hardBounced: number;
    spamReport: number;
    reject: number;

    undesirableCount: number;

    reliableOpens: number;
    mppOpens: number;
    mppRecipients: number;
    unsubscribeRate: number;
    bounceRate: number;
    reliableUniqueOpenCount: number;
    reliableUniqueOpenRate: number;
    reliableRecipients: number;

    negativeOutcome?: RecipientNegativeoutcomeStatsModel;

    constructor(apiModel: RecipientReportSeriesApiModel) {
        this.recipientCount = apiModel.total;
        this.sentCount = this.recipientCount;
        this.sentRatio = 1;

        if (apiModel.stats) {
            if (has(apiModel.stats, DistributionReportStat.DELIVERED)) {
                this.deliveredCount = apiModel.stats[DistributionReportStat.DELIVERED] || 0;
            } else {
                this.deliveredCount = (apiModel.stats[DistributionReportStat.RELIABLE_RECIPIENTS] || 0) +
                    (apiModel.stats[DistributionReportStat.MPP_RECIPIENTS] || 0);
            }

            this.deliveredRatio = AgprMath.safeDivide(this.deliveredCount, this.sentCount);

            if (has(apiModel.stats, DistributionReportStat.UNIQUE_OPEN)) {
                this.uniqueOpenCount = apiModel.stats[DistributionReportStat.UNIQUE_OPEN] || 0;
            } else {
                this.uniqueOpenCount = (apiModel.stats[DistributionReportStat.RELIABLE_UNIQUE_OPEN] || 0) +
                    (apiModel.stats[DistributionReportStat.MPP_OPEN] || 0);
            }

            this.openCount = apiModel.stats[DistributionReportStat.OPENS] || 0;
            this.mppOpens = apiModel.stats[DistributionReportStat.MPP_OPEN] || 0;
            this.reliableUniqueOpenCount = apiModel.stats[DistributionReportStat.RELIABLE_UNIQUE_OPEN] || 0;
            this.mppRecipients = apiModel.stats[DistributionReportStat.MPP_RECIPIENTS] || 0;
            this.reliableRecipients = apiModel.stats[DistributionReportStat.RELIABLE_RECIPIENTS] || 0;
            this.reliableOpens = this.openCount - this.mppOpens;
            this.uniqueOpenRatio = AgprMath.safeDivide(this.uniqueOpenCount, this.deliveredCount);
            this.reliableUniqueOpenRate = AgprMath.safeDivide(this.reliableUniqueOpenCount, this.reliableRecipients);

            if (has(apiModel.stats, DistributionReportStat.UNIQUE_CLICKS)) {
                this.uniqueClickCount = apiModel.stats[DistributionReportStat.UNIQUE_CLICKS] || 0;
            } else {
                this.uniqueClickCount = (apiModel.stats[DistributionReportStat.RELIABLE_UNIQUE_CLICKS] || 0) +
                    (apiModel.stats[DistributionReportStat.MPP_CLICKS] || 0);
            }
            this.clickCount = apiModel.stats[DistributionReportStat.CLICKS] || 0;
            this.uniqueClickRatio = AgprMath.safeDivide(this.uniqueClickCount, this.deliveredCount);
            this.reliableUniqueClicks = apiModel.stats[DistributionReportStat.RELIABLE_UNIQUE_CLICKS] || 0;
            this.mppClicks = apiModel.stats[DistributionReportStat.MPP_CLICKS] || 0;
            this.totalMppClicks = apiModel.stats[DistributionReportStat.TOTAL_MPP_CLICKS] || 0;
            this.reliableClicks = this.clickCount - this.totalMppClicks;
            this.reliableUniqueClickRate = AgprMath.safeDivide(this.reliableUniqueClicks, this.reliableRecipients);

            this.replyCount = apiModel.stats[DistributionReportStat.REPLY] || 0;
            this.replyRatio = AgprMath.safeDivide(this.replyCount, this.deliveredCount);

            this.softBounced = apiModel.stats[DistributionReportStat.SOFT_BOUNCED] || 0;
            this.hardBounced  = apiModel.stats[DistributionReportStat.HARD_BOUNCED] || 0;
            this.reject = apiModel.stats[DistributionReportStat.REJECT] || 0;
            this.spamReport = apiModel.stats[DistributionReportStat.SPAM_REPORT] || 0;
            this.unsubscribed = apiModel.stats[DistributionReportStat.UNSUBSCRIBED] || 0;
            this.previousUnsubscribe = apiModel.stats[DistributionReportStat.PREVIOUS_UNSUB] || 0;
            this.previousSoftBounced = apiModel.stats[DistributionReportStat.PREVIOUS_SOFT_BOUNCED] || 0;
            this.previousSpamReport = apiModel.stats[DistributionReportStat.PREVIOUS_SPAM_REPORT] || 0;
            this.previousHardBounced = apiModel.stats[DistributionReportStat.PREVIOUS_HARD_BOUNCED] || 0;

            this.unsubscribeRate = AgprMath.safeDivide(this.unsubscribed, this.sentCount) * 100;
            this.bounceRate = AgprMath.safeDivide((this.softBounced + this.hardBounced), this.sentCount) * 100;

            this.unopened = this.deliveredCount - this.uniqueOpenCount;
            this.blocked =
                this.softBounced
                + this.hardBounced
                + this.reject
                + this.previousUnsubscribe
                + this.previousSoftBounced
                + this.previousSpamReport
                + this.previousHardBounced;
            // @TODO: this.undesirableCount is never used outside of here, delete it?
            this.undesirableCount = this.softBounced + this.hardBounced + this.unsubscribed + this.reject + this.previousUnsubscribe;
        }
    }
}

export class IndividualRecipientReportDataModel extends RecipientReportDataSeries {
    listId?: number;
    searchId?: number;
    mailingListId?: number;
    recipientName: string;
    isRecommendedRecipient?: boolean;
    isStandaloneRecipient?: boolean;
    emailRecipients?: EmailRecipientModel[];

    constructor(apiModel: IndividualRecipientReportApiModel) {
        super(apiModel);
        this.listId = apiModel.listId;
        this.searchId = apiModel.searchId;
        this.mailingListId = apiModel.mailingListId;
    }
}

export class RecipientReportDataModel extends RecipientReportDataSeries {
    query;
    individualRecipientReports: IndividualRecipientReportDataModel[] = [];
    mailingListReports?: IndividualRecipientReportDataModel[];
    standaloneRecipientReport?: IndividualRecipientReportDataModel;
    benchmarkStats: DistributionAccountStatisticsModel;
    linkTexts: LinkText[];
    emailRecipients?: EmailRecipientModel[];

    get isBriefingReport(): boolean {
        return this.mailingListReports !== undefined || this.standaloneRecipientReport !== undefined;
    }

    estimatedActualUniqueOpens: number;
    estimatedActualOpens: number;

    estimatedActualUniqueClicks: number;
    estimatedActualClicks: number;

    deliveredRatioPercentageChangeFromBenchmark: number;
    uniqueOpenRatioPercentageChangeFromBenchmark: number;
    estimatedUniqueOpenRatioPercentageChangeFromBenchmark: number;
    uniqueClickRatePercentageChangeFromBenchmark: number;
    replyRatioPercentageChangeFromBenchmark: number;

    constructor(apiModel: DistributionRecipientReportApiModel, benchmarkStats?: DistributionAccountStatisticsModel) {
        super(apiModel);

        this.emailRecipients = [];
        apiModel.individualRecipientReports.forEach(item => {
            if (item.email) {
                this.emailRecipients.push(new EmailRecipientModel(item));
            }
        });
        this.linkTexts = apiModel.linkTexts;
        this.benchmarkStats = benchmarkStats;
        apiModel.individualRecipientReports.forEach(individualApiModel => {
            this.individualRecipientReports.push(new IndividualRecipientReportDataModel(individualApiModel));
        });

        this.mailingListReports = apiModel.mailingListReports?.map(report => {
            const model = new IndividualRecipientReportDataModel(report);
            model.recipientName = report.name;
            model.emailRecipients = report.individualRecipientReports
                .filter(item => item.email)
                .map(item => new EmailRecipientModel(item, report.mailingListId));
            return model;
        });

        if (apiModel.standaloneRecipientReport) {
            this.standaloneRecipientReport = new IndividualRecipientReportDataModel(apiModel.standaloneRecipientReport);
            this.standaloneRecipientReport.isStandaloneRecipient = true;
            this.standaloneRecipientReport.emailRecipients = apiModel.standaloneRecipientReport.individualRecipientReports
                .filter(item => item.email)
                .map(item => new EmailRecipientModel(item));
        }

        // rates that are NaN (divide by zero) will be treated as 0 when calculating count metrics
        if ((this.reliableUniqueOpenCount === 0) && (this.mppOpens > 0) && this.benchmarkStats) {
            this.estimatedActualUniqueOpens = Math.round((this.benchmarkStats.reliableUniqueOpenRate || 0) * this.mppOpens + this.reliableUniqueOpenCount);
            this.estimatedActualOpens = Math.round((this.benchmarkStats.reliableUniqueOpenRate || 0) * this.mppOpens + this.reliableOpens);

        } else {
            this.estimatedActualUniqueOpens = Math.round((this.reliableUniqueOpenRate || 0) * this.mppOpens + this.reliableUniqueOpenCount);
            this.estimatedActualOpens = Math.round((this.reliableUniqueOpenRate || 0) * this.mppOpens + this.reliableOpens);
        }

        this.estimatedActualUniqueClicks = Math.round((this.reliableUniqueClickRate || 0) * this.mppClicks + this.reliableUniqueClicks);
        this.estimatedActualClicks = Math.round((this.reliableUniqueClickRate || 0) * this.totalMppClicks + this.reliableClicks);

        if (this.benchmarkStats) {
            this.deliveredRatioPercentageChangeFromBenchmark = AgprMath.round((this.deliveredRatio * 100) - this.benchmarkStats.deliveryRate, 1);
            this.uniqueOpenRatioPercentageChangeFromBenchmark = AgprMath.round((this.uniqueOpenRatio * 100) - this.benchmarkStats.uniqueOpenRate, 1);
            this.estimatedUniqueOpenRatioPercentageChangeFromBenchmark = AgprMath.round((this.uniqueOpenRatio * 100) -
                this.benchmarkStats.mppAdjustedUniqueOpenRate, 1);
            this.uniqueClickRatePercentageChangeFromBenchmark = AgprMath.round((this.uniqueClickRatio * 100) - this.benchmarkStats.uniqueClickRate, 1);
            this.replyRatioPercentageChangeFromBenchmark = AgprMath.round((this.replyRatio * 100) - this.benchmarkStats.replyRate, 1);
        }
    }
}

